/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * ConnectLayout -- R13 web Round 1.
 *
 * Shell for /connect/* pages. Left tab rail on desktop; stacked top
 * tabs on screens <= 760px. Feature-gates the whole tree on
 * `features.connect` via `useConnectAccess`.
 *
 * Pages mount as children: ContactsListPage, ContactDetailPage,
 * FeedbackInboxPage, FeedbackDetailPage, SettingsPage.
 */

import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, Navigate } from 'react-router-dom';
import useConnectAccess from '../../hooks/useConnectAccess';

const TABS = [
  { path: '/connect/contacts', label: 'Contacts' },
  { path: '/connect/feedback', label: 'Feedback' },
  { path: '/connect/settings', label: 'Settings' }
];

const SKD_CYAN = '#06b6d4';
const SKD_CYAN_DARK = '#0e7490';

function _isWide() {
  if (typeof window === 'undefined') return true;
  return window.innerWidth > 760;
}

export default function ConnectLayout() {
  const { loading, hasAccess, user } = useConnectAccess();
  const location = useLocation();
  const [wide, setWide] = useState(_isWide);

  useEffect(() => {
    const onResize = () => setWide(_isWide());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (loading) {
    return <ConnectShell><div style={styles.loading}>Loading Connect…</div></ConnectShell>;
  }

  // Not logged in -> redirect to login with returnTo.
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  if (!token) {
    return <Navigate to={`/login?returnTo=${encodeURIComponent(location.pathname)}`} replace />;
  }

  if (!hasAccess) {
    return <ConnectShell><NotEnabledCard /></ConnectShell>;
  }

  return (
    <ConnectShell>
      <div style={{ display: 'flex', flexDirection: wide ? 'row' : 'column', gap: 24, alignItems: 'stretch' }}>
        <aside style={wide ? styles.sideNav : styles.topNav}>
          {TABS.map((t) => {
            const active = location.pathname === t.path || location.pathname.startsWith(t.path + '/');
            return (
              <Link
                key={t.path}
                to={t.path}
                style={wide
                  ? { ...styles.sideLink, ...(active ? styles.sideLinkActive : {}) }
                  : { ...styles.topLink, ...(active ? styles.topLinkActive : {}) }}
              >
                {t.label}
              </Link>
            );
          })}
          {user && user.fullName && (
            <div style={{
              marginTop: wide ? 'auto' : 10,
              paddingTop: wide ? 20 : 0,
              borderTop: wide ? '1px solid #e2e8f0' : 'none',
              color: '#94a3b8', fontSize: 12
            }}>
              Signed in as<br /><strong style={{ color: '#64748b' }}>{user.fullName}</strong>
            </div>
          )}
        </aside>
        <main style={{ flex: 1, minWidth: 0 }}>
          <Outlet />
        </main>
      </div>
    </ConnectShell>
  );
}

function ConnectShell({ children }) {
  return (
    <div style={styles.shell}>
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <Link to="/connect/contacts" style={styles.brand}>SkillDelivery <span style={{ color: SKD_CYAN }}>Connect</span></Link>
          <Link to="/" style={styles.backLink}>&larr; Back to app</Link>
        </div>
      </header>
      <div style={styles.page}>{children}</div>
    </div>
  );
}

function NotEnabledCard() {
  return (
    <div style={{
      background: 'white', padding: 32, borderRadius: 12, maxWidth: 560, margin: '40px auto',
      border: '1px solid #e2e8f0'
    }}>
      <h2 style={{ marginTop: 0, color: '#0f172a' }}>Request Connect access</h2>
      <p style={{ color: '#475569', fontSize: 15, lineHeight: 1.6 }}>
        Your account doesn't have SkillDelivery Connect enabled yet. Connect is
        the inviter + roadmap workspace for people you meet at events and
        introductions.
      </p>
      <p style={{ color: '#475569', fontSize: 15, lineHeight: 1.6 }}>
        Email us at <a href="mailto:info@skilldelivery.co.in" style={{ color: SKD_CYAN_DARK }}>info@skilldelivery.co.in</a> and
        we'll turn it on for your account.
      </p>
    </div>
  );
}

const styles = {
  shell: {
    minHeight: '100vh',
    background: '#f8fafc',
    color: '#0f172a',
    fontFamily: "'Segoe UI', Tahoma, sans-serif"
  },
  header: {
    background: 'white',
    borderBottom: '1px solid #e2e8f0'
  },
  headerInner: {
    maxWidth: 1200, margin: '0 auto', padding: '14px 20px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
  },
  brand: {
    fontSize: 18, fontWeight: 700, textDecoration: 'none', color: '#0f172a'
  },
  backLink: {
    fontSize: 13, color: '#64748b', textDecoration: 'none'
  },
  page: {
    maxWidth: 1200, margin: '0 auto', padding: '20px'
  },
  sideNav: {
    display: 'flex', flexDirection: 'column', gap: 4,
    width: 200, flexShrink: 0,
    padding: '12px 0'
  },
  topNav: {
    display: 'flex', flexDirection: 'row', gap: 4, flexWrap: 'wrap'
  },
  sideLink: {
    display: 'block', padding: '10px 14px', borderRadius: 8,
    textDecoration: 'none', color: '#475569', fontSize: 14, fontWeight: 500
  },
  sideLinkActive: {
    background: '#e0f2fe', color: SKD_CYAN_DARK, fontWeight: 700
  },
  topLink: {
    display: 'inline-block', padding: '8px 14px', borderRadius: 8,
    textDecoration: 'none', color: '#475569', fontSize: 13, fontWeight: 500,
    border: '1px solid transparent'
  },
  topLinkActive: {
    background: '#e0f2fe', color: SKD_CYAN_DARK, fontWeight: 700,
    border: '1px solid #bae6fd'
  },
  loading: {
    color: '#64748b', padding: 32, textAlign: 'center'
  }
};
